import { useEffect, useMemo, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Tooltip } from 'react-tooltip';
import LayoutSubmaster from '../../components/layoutSubmasterConfig/layoutSubmaster';
import { apiClientSubmaster } from '../../services/setupClientSubmaster/apiClientSubmaster';
import LocalStorage from '../../helpers/localstorage';
import handGloves from '../../components/dashboard/img/favicon/luvas.png';
import epiIcon from '../../components/dashboard/img/favicon/epi_cap.png';
import techIcon from '../../components/dashboard/img/favicon/tecnico.png';
import userIcon from '../../components/dashboard/img/favicon/users.png';

const calendarIcon = '🦺';

interface DashboardItem {
  data_due: string;
  funcionario: {
    name: string;
  };
  epi: {
    epiName: string;
  };
}

interface CalendarItem {
  data_due: string;
  employeesSubmaster: {
    name: string;
    employeeposition: string;
  };
  epirecord: {
    epiName: string;
    epitype: string;
    quantity: number;
    expirationDate?: string;
  };
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
}

function HomeSubmasterPage() {
  const storage = new LocalStorage();
  const token = String(storage.getAuthToken());
  const submasterId = Number(storage.getId());
  const [epiCount, setEpiCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [epiWarningCount, setEpiWarningCount] = useState(0);
  const [epiData, setEpiData] = useState<DashboardItem[]>([]);
  const [datesMap, setDatesMap] = useState<Record<string, CalendarItem[]>>({});

  useEffect(() => {
    const apiClient = apiClientSubmaster(token);

    async function fetchData() {
      try {
        const [epis, users, employees, warnings, dashboardData] = await Promise.all([
          apiClient.post(`/crud/getdata/EpirecordsSubmaster/${submasterId}/false`),
          apiClient.post(`/crud/getdata/UserSubmaster/${submasterId}/false`),
          apiClient.post(`/crud/getdata/EmployeesSubmaster/${submasterId}/false`),
          apiClient.post('/EpicloseToexpiration', { submasterId }),
          apiClient.post('/get/dashboard'),
        ]);

        setEpiCount(epis.data.length || 0);
        setUserCount(users.data.length || 0);
        const activeEmployees = Array.isArray(employees.data)
          ? employees.data.filter((item: { activeemployee?: boolean | null }) => item.activeemployee)
          : [];

        setEmployeeCount(activeEmployees.length || 0);
        setEpiWarningCount(warnings.data.qtd || 0);
        setEpiData(warnings.data.dataEpiExpiration || []);

        const map: Record<string, CalendarItem[]> = {};
        (dashboardData.data || []).forEach((item: CalendarItem) => {
          const dateKey = new Date(item.data_due).toISOString().split('T')[0];
          if (!map[dateKey]) {
            map[dateKey] = [];
          }
          map[dateKey].push(item);
        });
        setDatesMap(map);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      }
    }

    fetchData();
  }, [submasterId, token]);

  const summaryCards = useMemo(
    () => [
      {
        icon: handGloves,
        eyebrow: 'Validade crítica',
        value: epiWarningCount,
        caption: 'EPIs próximos do vencimento',
        accent: '#0d6efd',
        action: true,
      },
      {
        icon: epiIcon,
        eyebrow: 'Total de EPIs',
        value: epiCount,
        caption: 'EPIs ativos no cadastro',
        accent: '#2563eb',
      },
      {
        icon: techIcon,
        eyebrow: 'Usuários',
        value: userCount,
        caption: 'Acessos internos configurados',
        accent: '#4f8cff',
      },
      {
        icon: userIcon,
        eyebrow: 'Funcionários',
        value: employeeCount,
        caption: 'Colaboradores vinculados',
        accent: '#1d4ed8',
      },
    ],
    [employeeCount, epiCount, epiWarningCount, userCount]
  );

  const tileContent = ({
    date,
    view,
  }: {
    date: Date;
    view: string;
  }) => {
    if (view !== 'month') {
      return null;
    }

    const dateKey = date.toISOString().split('T')[0];
    const items = datesMap[dateKey];

    if (!items?.length) {
      return null;
    }

    const isExpiring = items.some(
      (item) => new Date(item.data_due) <= new Date()
    );

    return (
      <div style={{ textAlign: 'center' }}>
        <span
          data-tooltip-id={`tooltip-${dateKey}`}
          style={{
            cursor: 'pointer',
            fontSize: '18px',
            padding: '6px 10px',
            borderRadius: '12px',
            backgroundColor: isExpiring ? '#4f8cff' : '#0d6efd',
            color: '#fff',
            display: 'inline-block',
            fontWeight: 700,
          }}
        >
          {calendarIcon} {date.getDate()}
        </span>

        <Tooltip
          id={`tooltip-${dateKey}`}
          place="top"
          clickable
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            textAlign: 'left',
            padding: '0.8rem',
            width: '320px',
            borderRadius: '16px',
            boxShadow: '0 14px 30px rgba(0,0,0,0.18)',
            backgroundColor: '#fff',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {items.map((item, index) => {
              const employee = item.employeesSubmaster;
              const epi = item.epirecord;

              return (
                <div
                  key={index}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '12px',
                    backgroundColor: '#f6f9ff',
                    border: '1px solid rgba(13,110,253,0.08)',
                  }}
                >
                  <strong style={{ color: '#12313a' }}>{employee.name}</strong>
                  <div style={{ color: '#64808a', fontSize: '0.88rem' }}>
                    {employee.employeeposition}
                  </div>
                  <div style={{ color: '#12313a', marginTop: 6 }}>
                    {epi.epiName} • {epi.epitype} • Qtde {epi.quantity}
                  </div>
                </div>
              );
            })}
          </div>
        </Tooltip>
      </div>
    );
  };

  return (
    <LayoutSubmaster>
      <section className="dashboard-hero mb-4">
        <span className="pill-badge">Dashboard</span>
        <h1 className="dashboard-title">Operação diária com mais foco e leitura rápida</h1>
        <p className="dashboard-subtitle">
          Visualize os vencimentos de EPIs, usuários cadastrados e os principais
          indicadores da operação em um só lugar.
        </p>
      </section>

      <section className="dashboard-grid stats-4 mb-4">
        {summaryCards.map((card) => (
          <article
            key={card.eyebrow}
            className="dashboard-card metric-card"
            style={{ cursor: card.action ? 'pointer' : 'default' }}
            onClick={() => {
              if (!card.action) {
                return;
              }

              const modalElement = document.getElementById('epiModal');
              const bootstrapApi = (window as any).bootstrap;
              if (modalElement && bootstrapApi?.Modal) {
                bootstrapApi.Modal.getOrCreateInstance(modalElement).show();
              }
            }}
          >
            <div
              className="metric-card__icon"
              style={{ background: `${card.accent}1a` }}
            >
              <img src={card.icon} alt={card.eyebrow} />
            </div>
            <div>
              <p className="metric-card__eyebrow">{card.eyebrow}</p>
              <h2 className="metric-card__value">{card.value}</h2>
              <p className="metric-card__caption">{card.caption}</p>
            </div>
          </article>
        ))}
      </section>

      <div className="row">
        <div className="col-xl-5 mb-4">
          <section className="section-panel calendar-panel h-100">
            <div className="section-heading">
              <div>
                <h3>Agenda de vencimentos</h3>
                <p>Datas com EPIs e documentos que exigem atenção.</p>
              </div>
              <span className="pill-badge">{Object.keys(datesMap).length} dias mapeados</span>
            </div>
            <Calendar tileContent={tileContent} />
          </section>
        </div>

        <div className="col-xl-7 mb-4">
          <section className="section-panel h-100">
            <div className="section-heading">
              <div>
                <h3>Prioridades do painel</h3>
                <p>Resumo rápido para orientar a operação da empresa.</p>
              </div>
              <span className="pill-badge">Monitoramento contínuo</span>
            </div>

            <div className="status-list">
              <div className="status-item">
                <div className="status-item__meta">
                  <h4>EPIs próximos da validade</h4>
                  <p>
                    {epiWarningCount} item(ns) precisam de acompanhamento ou
                    renovação.
                  </p>
                </div>
                <div className="status-item__actions">
                  <span className="pill-badge">Alta prioridade</span>
                </div>
              </div>

              <div className="status-item">
                <div className="status-item__meta">
                  <h4>Base de usuários ativa</h4>
                  <p>{userCount} usuário(s) internos com acesso ao sistema.</p>
                </div>
                <div className="status-item__actions">
                  <span className="pill-badge">Operacional</span>
                </div>
              </div>

              <div className="status-item">
                <div className="status-item__meta">
                  <h4>Funcionários acompanhados</h4>
                  <p>
                    {employeeCount} colaborador(es) vinculados ao controle de
                    EPI.
                  </p>
                </div>
                <div className="status-item__actions">
                  <span className="pill-badge">Cadastro</span>
                </div>
              </div>

              <div className="status-item">
                <div className="status-item__meta">
                  <h4>Inventário disponível</h4>
                  <p>{epiCount} EPIs cadastrados no estoque da empresa.</p>
                </div>
                <div className="status-item__actions">
                  <span className="pill-badge">Estoque</span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className="modal fade" id="epiModal" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content shadow rounded-4 border-0">
            <div
              className="modal-header"
              style={{ background: 'linear-gradient(135deg, #0d6efd, #4f8cff)' }}
            >
              <h5 className="modal-title fw-bold text-white">
                EPIs próximos da validade
              </h5>
              <button
                type="button"
                className="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Fechar"
              />
            </div>

            <div className="modal-body">
              {epiData.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Funcionário</th>
                        <th>EPI</th>
                        <th>Validade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {epiData.map((item, index) => (
                        <tr key={index}>
                          <td>{item.funcionario.name}</td>
                          <td>{item.epi.epiName}</td>
                          <td>
                            <span
                              style={{
                                padding: '4px 10px',
                                borderRadius: '999px',
                                backgroundColor: 'rgba(37,99,235,0.08)',
                                color: '#2563eb',
                                fontWeight: 700,
                                display: 'inline-block',
                              }}
                            >
                              {formatDate(item.data_due)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-secondary mb-0">
                  Nenhum EPI próximo da validade encontrado.
                </p>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                data-bs-dismiss="modal"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    </LayoutSubmaster>
  );
}

export default HomeSubmasterPage;
